import { Button } from '@calizahq/react-components-v2'
import styled from '@emotion/styled'
import { Form, Formik, useFormikContext } from 'formik'
import React, { useMemo } from 'react'

import { BottomSheet, Checkbox, InputRadio } from '@/components'
import { useUserContext } from '@/context'
import { useTicketFiltersContext } from '@/context/filters/ticketFilters/ticketFilters'
import { TicketTypesQueries } from '@/graphql/queries/ticketTypes.queries'
import { useMobile, useToggle } from '@/hooks'
import { useGraphql } from '@/hooks/useGraphql'
import { Priority, Status } from '@/types/Ticket'
import { TicketType } from '@/types/TicketType'

import { statusOptions } from './components/StatusFilter'

const CONVERSATION_TYPE = 'Conversation'

const CountBubble = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #08786c;
  color: white;
  margin-left: 0.5rem;
  border-radius: 50%;
  width: 15px;
  height: 15px;
  font-size: 10px;
`

interface FormValues {
  priority?: string
  ticketStatuses?: string[]
  ticketTypes: string[]
}

const TypeCheckboxField = ({ option }: { option: { label: string; val: string } }) => {
  const { values, setFieldValue } = useFormikContext<FormValues>()
  const isChecked = values.ticketTypes?.includes(option.val) ?? false

  const handleClick = () => {
    const currentTypes = values.ticketTypes || []
    if (isChecked) {
      setFieldValue(
        'ticketTypes',
        currentTypes.filter((t) => t !== option.val),
      )
    } else {
      setFieldValue('ticketTypes', [...currentTypes, option.val])
    }
  }

  return (
    <div className="flex cursor-pointer flex-row items-center py-2 md:py-3.5" onClick={handleClick} role="presentation">
      <Checkbox checked={isChecked} onClick={handleClick} />
      <div className="text-sm">{option.label}</div>
    </div>
  )
}

export const MobileFilter = () => {
  const { setFilters, priority, ticketStatuses, ticketTypes } = useTicketFiltersContext()
  const { user } = useUserContext()
  const isSuperAdmin = !!user?.isSuperAdmin
  const { isShown: modalShown, toggle: modalToggle } = useToggle()
  const { isMobile } = useMobile()
  const priorities = Object.values(Priority)
  const { data: typesData, isLoading } = useGraphql<{ ticketTypes: TicketType[] }>(
    TicketTypesQueries.QUERY_KEY,
    TicketTypesQueries.getTicketTypes(),
  )

  const ticketTypeOptions = useMemo(() => {
    return !isLoading && typesData?.ticketTypes
      ? typesData?.ticketTypes
          .map((t) => ({ val: t.description, label: t.description }))
          .filter((t) => isSuperAdmin || t.val !== CONVERSATION_TYPE)
          .sort((a, b) => a.label.localeCompare(b.label))
      : []
  }, [typesData?.ticketTypes, isLoading, isSuperAdmin])

  const selectedFilterCount = useMemo(() => {
    let count = 0
    if (priority) count++
    if (ticketStatuses && ticketStatuses.length > 0) count++
    if (ticketTypes && ticketTypes.length > 0) count++
    return count
  }, [priority, ticketStatuses, ticketTypes])

  const capitalize = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1)
  }

  const handleApplyFilters = (val) => {
    setFilters({
      ticketTypes: val.ticketTypes && val.ticketTypes.length > 0 ? val.ticketTypes : null,
      ticketStatuses: val.ticketStatuses && val.ticketStatuses != 'null' ? val.ticketStatuses.split(',') : null,
      priority: val.priority ? val.priority : null,
    })
    modalToggle()
  }

  const handleResetFilters = () => {
    setFilters({
      ticketTypes: null,
      ticketStatuses: [Status.NEW, Status.WORKING, Status.NEEDS_ACTION, Status.AWAITING_RESPONSE],
      priority: null,
    })
    modalToggle()
  }

  return (
    <div className="flex lg:hidden">
      <Button
        className="whitespace-nowrap bg-action-secondary px-3 text-label-sm"
        onClick={modalToggle}
        size="small"
        variant="secondary"
      >
        <div className="flex items-center">
          Filter
          {selectedFilterCount > 0 && <CountBubble>{selectedFilterCount}</CountBubble>}
        </div>
      </Button>
      <Formik
        enableReinitialize={true}
        initialValues={{
          priority: priority || undefined,
          ticketStatuses: ticketStatuses ? ticketStatuses.join(',') : undefined,
          ticketTypes: ticketTypes || [],
        }}
        onSubmit={handleApplyFilters}
      >
        {({ submitForm }) => (
          <Form>
            <BottomSheet
              header="Filters"
              isVisible={modalShown}
              mobileFullHeight={false}
              onClose={modalToggle}
              renderFooter={() => (
                <div className="flex flex-row items-center justify-between px-6 pb-4 pt-4">
                  {isMobile ? (
                    <Button
                      className="mr-4 flex-1 bg-grey-100"
                      onClick={handleResetFilters}
                      type="button"
                      variant="secondary"
                    >
                      Reset
                    </Button>
                  ) : (
                    <Button onClick={handleResetFilters} size="small" type="button" variant="secondary">
                      <span className="underline underline-offset-2">Reset</span>
                    </Button>
                  )}
                  <Button
                    className="flex-1 bg-text-semantic-success md:flex-0"
                    onClick={submitForm}
                    size={isMobile ? undefined : 'small'}
                  >
                    Apply
                  </Button>
                </div>
              )}
            >
              <div className="flex w-full flex-col px-6">
                <div className="border-b border-grey-300 pb-4">
                  <h6 className="text-label-xs text-grey-600">Priorities</h6>

                  {priorities.map((option, index) => {
                    return (
                      <InputRadio
                        className="last:pb-0 md:first:mt-3 md:last:pb-4"
                        id={option}
                        key={index + option}
                        label={option}
                        name="priority"
                        value={option}
                      />
                    )
                  })}
                </div>
                <div className="border-b border-grey-300 py-4">
                  <h6 className="text-label-xs text-grey-600">Status</h6>
                  {statusOptions.map((option, index) => {
                    return (
                      <InputRadio
                        className="last:pb-0 md:first:mt-3 md:last:pb-4"
                        id={option.label}
                        key={index + option.label}
                        label={capitalize(option.label)}
                        name="ticketStatuses"
                        value={option.val + ''}
                      />
                    )
                  })}
                </div>
                <div className="py-4">
                  <h6 className="text-label-xs text-grey-600">Type</h6>

                  {ticketTypeOptions.map((option, index) => (
                    <TypeCheckboxField key={index + option.label} option={option} />
                  ))}
                </div>
              </div>
            </BottomSheet>
          </Form>
        )}
      </Formik>
    </div>
  )
}
